import { useEffect, useState, useCallback } from 'react';
import { getMobileToday, markNotificationRead, markAllRead, updatePostHuddleAction } from '../../lib/api';
import { Bell, CheckCircle, Fire, ArrowRight, CaretRight, Siren, Sparkle, Warning, Calendar, UserCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MobileLoanDetail from './MobileLoanDetail';

function TimeAgo({ iso }) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  let label;
  if (diff < 60) label = 'now';
  else if (diff < 3600) label = `${Math.floor(diff / 60)}m`;
  else if (diff < 86400) label = `${Math.floor(diff / 3600)}h`;
  else label = `${Math.floor(diff / 86400)}d`;
  return <span className="text-[10px] text-muted-foreground">{label}</span>;
}

function UrgencyChip({ urgency }) {
  const cls = urgency === 'Critical' ? 'chip-critical' : urgency === 'High' ? 'chip-warn' : urgency === 'Normal' ? 'chip-neutral' : 'chip-info';
  return <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded border ${cls} uppercase tracking-wider`}>{urgency}</span>;
}

export default function MobileToday({ user, onOpenTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFile, setOpenFile] = useState(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await getMobileToday(user?.initials || '');
      setData(d);
    } catch (e) {
      setError('Could not load. Tap to retry.');
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  // Refresh on focus / reconnect
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    const onOnline = () => load();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('online', onOnline); };
  }, [load]);

  const handleNotifTap = async (n) => {
    try { await markNotificationRead(n.id); } catch (e) { /* non-fatal */ }
    if (n.file_id) setOpenFile(n.file_id);
    else if (n.deep_link) onOpenTab?.(n.deep_link);
    load();
  };
  const handleAckAll = async () => {
    try { await markAllRead(); toast.success('Updates marked read'); load(); } catch (e) { toast.error('Could not mark read'); }
  };
  const handleCompleteAction = async (actionId) => {
    try {
      await updatePostHuddleAction(actionId, { status: 'Completed' });
      toast.success('Task completed', { action: { label: 'Undo', onClick: async () => { await updatePostHuddleAction(actionId, { status: 'Pending' }); load(); } } });
      load();
    } catch (e) { toast.error('Could not complete'); }
  };

  if (loading && !data) {
    return <div className="p-4 space-y-3" data-testid="mobile-today-loading">
      {[0,1,2].map(i => <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />)}
    </div>;
  }
  if (error && !data) {
    return <div className="p-4 text-center" data-testid="mobile-today-error">
      <p className="text-sm text-muted-foreground mb-3">{error}</p>
      <button onClick={load} className="mr-btn-primary px-4 py-2 rounded-lg text-sm">Retry</button>
    </div>;
  }

  const { team_updates, needs_attention, today_actions, pipeline } = data || {};
  const hasUnread = team_updates?.unread_count > 0;
  const visibleUpdates = showAllUpdates ? team_updates.unread : team_updates?.unread?.slice(0, 3);

  return (
    <div className="pb-4 space-y-3" data-testid="mobile-today">
      {/* Compact header - name + date */}
      <div className="flex items-center justify-between px-1 pt-1">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Today · {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</p>
          <h1 className="text-lg font-bold text-foreground leading-tight">Hi, {user?.name?.split(' ')[0] || 'there'}</h1>
        </div>
        <button onClick={load} className="text-[10px] font-semibold text-muted-foreground px-2 py-1 rounded border border-border" data-testid="today-refresh">Refresh</button>
      </div>

      {/* 1. TEAM UPDATES (first) */}
      <section data-testid="section-team-updates">
        {hasUnread ? (
          <div className="mr-card-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Bell size={15} weight="fill" className="text-[color:var(--mr-red)]" />
                <h2 className="text-sm font-bold text-foreground">Team updates</h2>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded chip-critical border">{team_updates.unread_count}</span>
              </div>
              {team_updates.unread_count > 1 && (
                <button onClick={handleAckAll} className="text-[11px] font-semibold text-[color:var(--mr-blue)]" data-testid="ack-all-btn">Mark all read</button>
              )}
            </div>
            <div className="space-y-1.5">
              {visibleUpdates.map(n => (
                <button key={n.id} onClick={() => handleNotifTap(n)} data-testid={`today-notif-${n.id}`}
                  className="w-full text-left flex items-start gap-2 p-2 rounded-lg bg-muted/30 hover:bg-muted/60 border border-border/60 min-h-[44px]">
                  <span className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0 ${n.severity === 'critical' ? 'chip-critical' : 'chip-info'} border`}>{(n.from_member || '•').slice(0,2).toUpperCase()}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold text-foreground truncate">{n.from_member || 'Team'}</span>
                      {n.borrower && <span className="text-[10px] text-muted-foreground truncate">· {n.borrower}</span>}
                      <TimeAgo iso={n.created_at} />
                    </div>
                    <p className="text-xs text-foreground leading-snug line-clamp-2">{n.message}</p>
                    {n.action_required && <span className="inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider chip-warn border px-1.5 py-0.5 rounded">Action needed</span>}
                  </div>
                  <CaretRight size={14} className="text-muted-foreground flex-shrink-0 mt-1.5" />
                </button>
              ))}
            </div>
            {team_updates.has_more && !showAllUpdates && (
              <button onClick={() => setShowAllUpdates(true)} className="w-full mt-2 text-[11px] font-semibold text-[color:var(--mr-blue)] py-1.5 border-t border-border" data-testid="today-view-all-updates">
                View all {team_updates.unread_count} updates
              </button>
            )}
          </div>
        ) : (
          <div className="mr-card p-3 flex items-center gap-2.5" data-testid="all-caught-up">
            <CheckCircle size={18} weight="fill" className="text-[color:var(--mr-ok)]" />
            <div>
              <p className="text-xs font-bold text-foreground">All caught up on team updates</p>
              <p className="text-[11px] text-muted-foreground">Scroll for files that need attention →</p>
            </div>
          </div>
        )}
      </section>

      {/* 2. FILES NEEDING ATTENTION */}
      <section data-testid="section-needs-attention">
        <div className="flex items-center justify-between px-1 mb-1.5">
          <div className="flex items-center gap-1.5">
            <Fire size={14} weight="fill" className="text-[color:var(--mr-red)]" />
            <h2 className="text-sm font-bold text-foreground">Files needing attention</h2>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded chip-warn border">{needs_attention?.count || 0}</span>
          </div>
          {needs_attention?.has_more && (
            <button onClick={() => onOpenTab?.('mobile-pipeline?filter=needs-attention')} className="text-[11px] font-semibold text-[color:var(--mr-blue)]" data-testid="view-all-attention">
              See all →
            </button>
          )}
        </div>
        {needs_attention?.top?.length ? (
          <div className="space-y-1.5">
            {needs_attention.top.map(f => (
              <button key={f.id} onClick={() => setOpenFile(f.id)} data-testid={`attention-${f.id}`}
                className="w-full text-left mr-card p-2.5 hover:border-[color:var(--mr-red)] min-h-[44px]">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-bold text-foreground truncate">{f.borrower}</span>
                      <UrgencyChip urgency={f.urgency} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{f.reasons.slice(0,3).join(' · ')}</p>
                  </div>
                  <CaretRight size={14} className="text-muted-foreground flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mr-card p-3 text-center">
            <CheckCircle size={18} weight="duotone" className="text-[color:var(--mr-ok)] mx-auto mb-1" />
            <p className="text-[11px] text-muted-foreground">No files need urgent attention right now.</p>
          </div>
        )}
      </section>

      {/* 3. TODAY'S ACTIONS */}
      <section data-testid="section-today-actions">
        <div className="flex items-center justify-between px-1 mb-1.5">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-1.5">
            <Calendar size={14} weight="fill" className="text-[color:var(--mr-blue)]" />
            Your next actions
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded chip-info border">{today_actions?.total || 0}</span>
          </h2>
          <button onClick={() => onOpenTab?.('mobile-tasks')} className="text-[11px] font-semibold text-[color:var(--mr-blue)]" data-testid="view-all-tasks">All tasks →</button>
        </div>
        {today_actions?.overdue?.length > 0 && (
          <div className="mb-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--mr-red-strong)] px-1 mb-1">Overdue · {today_actions.overdue.length}</p>
            {today_actions.overdue.slice(0,3).map(a => (
              <ActionRow key={a.id} action={a} onComplete={handleCompleteAction} onOpenFile={() => setOpenFile(a.file_id)} tone="critical" />
            ))}
          </div>
        )}
        {today_actions?.today?.length > 0 && (
          <div className="mb-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1 mb-1">Due today</p>
            {today_actions.today.slice(0,3).map(a => (
              <ActionRow key={a.id} action={a} onComplete={handleCompleteAction} tone="warn" />
            ))}
          </div>
        )}
        {!today_actions?.overdue?.length && !today_actions?.today?.length && (
          <div className="mr-card p-3 text-center">
            <p className="text-[11px] text-muted-foreground">Nothing due today. Nice work.</p>
          </div>
        )}
      </section>

      {/* 4. COMPACT PIPELINE OVERVIEW */}
      <section data-testid="section-pipeline-overview">
        <div className="flex items-center justify-between px-1 mb-1.5">
          <h2 className="text-sm font-bold text-foreground">Pipeline</h2>
          <button onClick={() => onOpenTab?.('mobile-pipeline')} className="text-[11px] font-semibold text-[color:var(--mr-blue)]" data-testid="open-pipeline">Open pipeline →</button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <div className="mr-card p-2.5">
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Active</p>
            <p className="text-xl font-black text-foreground leading-tight">{pipeline?.total_active || 0}</p>
          </div>
          <div className="mr-card p-2.5">
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Attention</p>
            <p className="text-xl font-black text-[color:var(--mr-red-strong)] leading-tight">{pipeline?.needs_attention || 0}</p>
          </div>
          <div className="mr-card p-2.5">
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Closing &lt;7d</p>
            <p className="text-xl font-black text-[color:var(--mr-blue-strong)] leading-tight">{pipeline?.closing_this_week || 0}</p>
          </div>
        </div>
      </section>

      {openFile && <MobileLoanDetail fileId={openFile} onClose={() => { setOpenFile(null); load(); }} currentUser={user} />}
    </div>
  );
}

function ActionRow({ action, onComplete, tone, onOpenFile }) {
  const toneCls = tone === 'critical' ? 'border-[color:#d9363e40]' : tone === 'warn' ? 'border-[color:#a8611640]' : 'border-border';
  return (
    <div className={`mr-card p-2.5 mb-1 border ${toneCls}`} data-testid={`action-row-${action.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={() => onComplete(action.id)} data-testid={`complete-action-${action.id}`}
          className="w-6 h-6 rounded-full border-2 border-muted-foreground hover:border-[color:var(--mr-ok)] hover:bg-[color:var(--mr-ok-wash)] flex-shrink-0 mt-0.5 flex items-center justify-center">
          <CheckCircle size={14} className="opacity-0 hover:opacity-100 text-[color:var(--mr-ok)]" />
        </button>
        <div className="flex-1 min-w-0" onClick={onOpenFile}>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-foreground truncate">{action.borrower || 'Task'}</span>
            {action.deadline && <span className="text-[10px] text-muted-foreground">· {action.deadline}</span>}
          </div>
          <p className="text-[11px] text-foreground/80 leading-snug line-clamp-2">{action.action}</p>
        </div>
      </div>
    </div>
  );
}
